import { useState, useRef, useEffect, useCallback } from 'react';
import { communities, categories, regions, sizes, activityLevels, sortOptions } from '@/mocks/communities';
import GlowButton from '@/components/base/GlowButton';

interface SidebarProps {
  selectedCategory: string;
  selectedRegion: string;
  selectedSize: string;
  selectedActivity: string;
  selectedSort: string;
  onCategoryChange: (v: string) => void;
  onRegionChange: (v: string) => void;
  onSizeChange: (v: string) => void;
  onActivityChange: (v: string) => void;
  onSortChange: (v: string) => void;
  onResetAll: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

interface FilterSectionProps {
  title: string;
  icon: string;
  options: string[];
  selected: string;
  onChange: (v: string) => void;
}

function FilterSection({ title, icon, options, selected, onChange }: FilterSectionProps) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-[#b8d4ea]/40 pb-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full mb-3 cursor-pointer group"
      >
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-[#0088CC]">
            <i className={`${icon} text-sm`}></i>
          </span>
          <span className="text-sm font-semibold text-slate-700">{title}</span>
        </div>
        <i className={`ri-arrow-down-s-line text-slate-400 text-sm transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && (
        <div className="flex flex-col gap-1">
          {options.map((opt) => (
            <GlowButton
              key={opt}
              onClick={() => onChange(opt)}
              className="w-full text-xs px-3 py-2 rounded-lg !justify-start"
              variant={selected === opt ? 'primary' : 'secondary'}
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${selected === opt ? 'bg-white' : 'bg-slate-400'}`}></span>
              {opt}
            </GlowButton>
          ))}
        </div>
      )}
    </div>
  );
}

const topCommunities = [...communities].sort((a, b) => b.memberCount - a.memberCount).slice(0, 4);
const maxMembers = topCommunities[0]?.memberCount ?? 1;
const TOTAL_CELLS = 8;

function BatteryBar({ value, max }: { value: number; max: number }) {
  const ratio = Math.min(value / max, 1);
  const filled = Math.round(ratio * TOTAL_CELLS);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: TOTAL_CELLS }).map((_, i) => (
        <span
          key={i}
          className={`block w-2.5 h-3 rounded-sm border transition-all ${
            i < filled ? 'bg-[#0088CC]/65 border-[#0088CC]/70' : 'bg-transparent border-[#b8d4ea]/50'
          }`}
        />
      ))}
    </div>
  );
}

const SCROLL_CELLS = 20;
const S_CELL_W = 8;
const S_CELL_H = 5;
const S_GAP = 8;

function SidebarScrollBar({ scrollEl }: { scrollEl: HTMLDivElement | null }) {
  const [progress, setProgress] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  useEffect(() => {
    if (!scrollEl) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollEl;
      const max = scrollHeight - clientHeight;
      setProgress(max > 0 ? scrollTop / max : 0);
    };
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [scrollEl]);

  const scrollToRatio = useCallback((ratio: number) => {
    if (!scrollEl) return;
    const max = scrollEl.scrollHeight - scrollEl.clientHeight;
    if (max <= 0) return;
    scrollEl.scrollTop = Math.max(0, Math.min(1, ratio)) * max;
  }, [scrollEl]);

  const getRatioFromEvent = useCallback((clientY: number) => {
    const el = containerRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const cellTotalH = S_CELL_H * SCROLL_CELLS + S_GAP * (SCROLL_CELLS - 1);
    const relY = clientY - rect.top - (rect.height - cellTotalH) / 2;
    return Math.max(0, Math.min(1, relY / cellTotalH));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    scrollToRatio(getRatioFromEvent(e.clientY));
    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      ev.preventDefault();
      scrollToRatio(getRatioFromEvent(ev.clientY));
    };
    const onMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove, { passive: false });
    window.addEventListener('mouseup', onMouseUp);
  }, [getRatioFromEvent, scrollToRatio]);

  const handleCellClick = useCallback((index: number) => {
    scrollToRatio(SCROLL_CELLS <= 1 ? 0 : index / (SCROLL_CELLS - 1));
  }, [scrollToRatio]);

  const filled = Math.round(progress * SCROLL_CELLS);

  const getCellStyle = (i: number) => {
    const isFilledByScroll = i < filled;
    const isFilledByHover = hoveredIndex !== null && i <= hoveredIndex;
    if (isFilledByHover && isFilledByScroll) return 'bg-[#5ab0d8] border-[#5ab0d8] shadow-[0_0_10px_4px_rgba(0,136,204,0.35)]';
    if (isFilledByHover) return 'bg-[#0088CC]/30 border-[#0088CC]/50';
    if (isFilledByScroll) return 'bg-[#0088CC]/70 border-[#0088CC]/80 shadow-[0_0_6px_2px_rgba(0,136,204,0.25)]';
    return 'bg-transparent border-[#b8d4ea]/60';
  };

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={() => setHoveredIndex(null)}
      className="flex flex-col items-center py-1 shrink-0 cursor-pointer select-none"
      style={{ gap: S_GAP }}
    >
      {Array.from({ length: SCROLL_CELLS }).map((_, i) => (
        <button
          key={i}
          onClick={() => handleCellClick(i)}
          onMouseEnter={() => setHoveredIndex(i)}
          style={{ width: S_CELL_W, height: S_CELL_H }}
          className={`block rounded-[2px] border transition-all duration-100 cursor-pointer ${getCellStyle(i)}`}
        />
      ))}
    </div>
  );
}

export default function Sidebar({
  selectedCategory, selectedRegion, selectedSize, selectedActivity, selectedSort,
  onCategoryChange, onRegionChange, onSizeChange, onActivityChange, onSortChange,
  onResetAll, isMobileOpen, onMobileClose,
}: SidebarProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [mobileScrollEl, setMobileScrollEl] = useState<HTMLDivElement | null>(null);

  const setRef = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node ?? undefined as unknown as HTMLDivElement;
    setScrollEl(node);
  }, []);

  const setMobileRef = useCallback((node: HTMLDivElement | null) => {
    setMobileScrollEl(node);
  }, []);

  const formatCount = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toString();
  const activityColor = (level: string) => {
    if (level === 'high') return 'bg-emerald-500';
    if (level === 'medium') return 'bg-amber-500';
    return 'bg-slate-400';
  };

  const sidebarContent = (
    <div className="flex flex-col gap-4">
      {/* Sort */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Sort By</p>
        <div className="flex flex-wrap gap-1.5">
          {sortOptions.map((opt) => (
            <GlowButton
              key={opt}
              onClick={() => onSortChange(opt)}
              className="text-xs px-3 py-1.5 rounded-full"
              variant={selectedSort === opt ? 'primary' : 'secondary'}
            >
              {opt}
            </GlowButton>
          ))}
        </div>
      </div>

      <FilterSection title="Category" icon="ri-apps-line" options={categories} selected={selectedCategory} onChange={onCategoryChange} />
      <FilterSection title="Region" icon="ri-global-line" options={regions} selected={selectedRegion} onChange={onRegionChange} />
      <FilterSection title="Community Size" icon="ri-group-line" options={sizes} selected={selectedSize} onChange={onSizeChange} />
      <FilterSection title="Activity Level" icon="ri-pulse-line" options={activityLevels} selected={selectedActivity} onChange={onActivityChange} />

      {/* Top Communities */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Top Communities</p>
        <div className="flex flex-col gap-2">
          {topCommunities.map((c, i) => (
            <a
              key={c.id}
              href={c.telegramLink}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-3 p-2.5 rounded-xl bg-white/55 hover:bg-white/85 border border-[#c5d9eb]/50 hover:border-[#0088CC]/25 transition-all cursor-pointer group"
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0088CC]/10 text-[#0088CC] text-xs font-bold shrink-0">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-600 truncate group-hover:text-slate-800">{c.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <BatteryBar value={c.memberCount} max={maxMembers} />
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">{formatCount(c.memberCount)}</span>
                </div>
              </div>
              <span className={`w-2 h-2 rounded-full shrink-0 ${activityColor(c.activityLevel)}`}></span>
            </a>
          ))}
        </div>
      </div>

      {/* Side Ad */}
      <div className="rounded-xl border border-dashed border-[#0088CC]/30 bg-white/55 backdrop-blur-sm p-4 text-center">
        <span className="text-[10px] text-[#0088CC] font-semibold uppercase tracking-wider">Advertisement</span>
        <div className="mt-2 w-full h-32 rounded-lg overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=minimalist%20frosted%20glass%20abstract%20soft%20pale%20blue%20white%20mist%20translucent%20overlapping%20geometric%20shapes%20subtle%20gradient%20clean%20light%20background%20serene%20elegant%20modern%20art%20panel&width=240&height=128&seq=s101&orientation=landscape"
            alt="Advertisement"
            className="w-full h-full object-cover object-top rounded-lg"
          />
        </div>
        <p className="text-xs text-slate-400 mt-2">Promote your community here</p>
        <a href="#" className="text-xs text-[#0088CC] hover:underline cursor-pointer">Contact us</a>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 xl:w-72 shrink-0 sticky top-20 h-[calc(100vh-5rem)] overflow-hidden">
        <div className="bg-[#ddeaf5]/70 backdrop-blur-xl rounded-2xl p-4 h-full flex flex-row gap-2 overflow-hidden">
          <div
            ref={setRef}
            className="flex-1 overflow-y-auto flex flex-col gap-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-800">Filters</h2>
              <button onClick={onResetAll} className="text-xs text-[#0088CC] hover:underline cursor-pointer whitespace-nowrap">Reset all</button>
            </div>
            {sidebarContent}
          </div>
          <div className="flex flex-col items-center justify-center py-2">
            <SidebarScrollBar scrollEl={scrollEl} />
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Drawer */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={onMobileClose}></div>
          <div className="relative w-72 max-w-[85vw] bg-white/90 backdrop-blur-xl border-r border-slate-200/60 h-full flex flex-row z-10">
            <div
              ref={setMobileRef}
              className="flex-1 overflow-y-auto flex flex-col gap-4 p-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' } as React.CSSProperties}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-800">Filters</h2>
                <button onClick={onMobileClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              {sidebarContent}
            </div>
            <div className="flex flex-col items-center justify-center py-2 pr-2">
              <SidebarScrollBar scrollEl={mobileScrollEl} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
